import * as React from "react"
import styled from "styled-components"
import { theme } from "../theme"
import { Col, Container, Hidden, Row, Visible } from "react-grid-system"
import { HorizontalSpacer, Spacer } from "../atoms/spacers"
import { MediaQuery } from "../../utils/design-helpers"
import H4 from "../atoms/typography/H4"
import LoadingSpinner from "../atoms/LoadingSpinner"
import { FadeInWhenInView } from "../atoms/animations/FadeInWhenInView"
import CaseCarousel from "../molecules/HomePageComponents/CaseCarousel"
import KuBanner from "../molecules/HomePageComponents/KuBanner"
import NewsCarousel from "../molecules/HomePageComponents/NewsCarousel"
import H1 from "../atoms/typography/H1"
import H2 from "../atoms/typography/H2"
import AbcNewsLogo from "../atoms/icons/images/abcnewslogo.svg"
import ItvLogo from "../atoms/icons/images/itvlogo.svg"
import CbsLogo from "../atoms/icons/images/cbslogo.svg"
import FnLogo from "../atoms/icons/images/fnlogo.svg"
import HowItWorksBanner from "../molecules/HomePageComponents/NewHowItWorksBanner"
import { Link } from "react-router-dom"
import ZoomOnScroll from "../molecules/HomePageComponents/ZoomOnScroll"
import { VideoPlayToggle } from "../molecules/VideoPlayToggle"
import Subtitle from "../atoms/typography/Subtitle"
import { captureExceptionWithContext } from "../../utils/sentry"
import Button from "../atoms/buttons/Button"
import { useArticleCollection } from "../../hooks/news/useArticleCollection"
import { useHomePageImageCollection } from "../../hooks/home/useHomePageImageCollection"
import { useHomePage } from "../../hooks/home/useHomePage"

const HeroSubtitle = styled.div`
  position: absolute;
  bottom: 10%;
  left: 10%;

  ${MediaQuery.XS} {
    left: 0%;
    bottom: ${({ theme }) => theme.spacings.large};
  }
`

const RelativeDiv = styled.div`
  position: relative;
  height: 100%;
`

const PartnerBanner = styled.div`
  width: 100%;
  padding: 2rem;
  min-height: 780px;
  background-color: ${(props) => props.theme.colors.greyLighter};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const AudienceSegment = styled.div`
  width: 100%;
  padding: 2rem;
  min-height: 780px;
  background-color: ${(props) => props.theme.colors.greyLighter};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  ${MediaQuery.XS} {
    padding: 1rem;
  }
`

const AudienceImage = styled.img`
  width: 100%;
  height: 500px;
  object-fit: cover;
  box-shadow: 0px 0px 4px 0px rgba(119, 119, 119, 0.25);
  border-radius: 1.5rem;
  overflow: clip;
`

const AudienceTitle = styled.div`
  position: absolute;
  top: 0px;
  right: 16px;
  left: 16px;
  background-image: linear-gradient(to bottom, rgba(245, 246, 252, 0.185), rgba(30, 29, 30, 0.73));
  height: 500px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  border-radius: 1.5rem;
  padding: 1rem;
`

const VideoPlayToggleContainer = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  margin: 4rem;
`

const HomePage: React.FunctionComponent = () => {
  const { data: newsArticleData, loading, error } = useArticleCollection(0, 5, "")
  const { homePageContentCollection } = useHomePageImageCollection()
  const { homePageSecondSection } = useHomePage()

  const articles = newsArticleData?.articleCollection.items

  const heroImageUrl =
    homePageContentCollection?.items[0].heroImage.url ??
    "https://images.ctfassets.net/kzqg847wz5jr/6YsVPK6Dic5HNCbBiKI7k9/12e3a662b3de8fe2aec99c6f48549bf3/satcube_hero_poster_image.png"
  const secondSection = homePageSecondSection?.items[0] ?? undefined

  if (error) {
    captureExceptionWithContext(error, "error fetching news from contentful")
  }

  const videoRef = React.useRef<any>(undefined)

  React.useEffect(() => {
    document.title = "Satcube"
    videoRef.current.defaultMuted = true
  }, [])

  return (
    <div>
      <ZoomOnScroll before={true}>
        <RelativeDiv>
          <video
            ref={videoRef}
            src={heroImageUrl}
            poster="https://images.ctfassets.net/kzqg847wz5jr/6YsVPK6Dic5HNCbBiKI7k9/12e3a662b3de8fe2aec99c6f48549bf3/satcube_hero_poster_image.png"
            style={{
              marginBottom: -8,
              width: "100%",
              height: "100vh",
              objectFit: "cover",
            }}
            loop
            autoPlay
            muted
            playsInline
          >
            Your browser does not support the video tag.
          </video>
          <Hidden xs sm md>
            <VideoPlayToggleContainer>
              <VideoPlayToggle videoRef={videoRef} />
            </VideoPlayToggleContainer>
          </Hidden>
          <HeroSubtitle>
            <Container>
              <Col md={8}>
                <FadeInWhenInView>
                  <H1 color={theme.colors.white} textAlign="left">
                    Enabling quick access to seamless satellite broadband
                  </H1>
                </FadeInWhenInView>
                <HorizontalSpacer spacing={2} />
              </Col>
              <Row nogutter>
                <Col xs={12} md="content">
                  <Link to="/contact">
                    <Button title="Contact us" size="big" variant="blue" />
                  </Link>
                </Col>
                <Hidden xs sm>
                  <Spacer spacing={4} />
                </Hidden>
                <Col xs={12} md="content">
                  <Visible xs sm>
                    <HorizontalSpacer spacing={2} />
                  </Visible>
                  <Link to="/store">
                    <Button title="Shop online" size="big" variant="green" />
                  </Link>
                </Col>
              </Row>
              <Visible sm md>
                <HorizontalSpacer spacing={2} />
                <Row justify="end">
                  <Col xs="content">
                    <VideoPlayToggle videoRef={videoRef} />
                  </Col>
                </Row>
              </Visible>
            </Container>
          </HeroSubtitle>
        </RelativeDiv>
      </ZoomOnScroll>
      <PartnerBanner>
        <Container>
          <Row justify="center">
            <H2 textAlign="center">
              Thanks to our game-changing portable satellite terminals, Satcube is trusted by leading organizations all
              around the world to guarantee their access to high-speed broadband anywhere in the world, quickly and
              efficiently.
            </H2>
          </Row>
          <HorizontalSpacer spacing={5.5} />
          <Row justify="center">
            <H4 textAlign="center">Some of our partners:</H4>
          </Row>
          <Row justify="around">
            <AbcNewsLogo />
            <ItvLogo />
            <CbsLogo />
            <FnLogo />
          </Row>
        </Container>
      </PartnerBanner>

      <AudienceSegment>
        <Container>
          <HorizontalSpacer spacing={3} />
          <H2 color={theme.colors.greyDarker}>Whatever you do</H2>
          <HorizontalSpacer spacing={1} />
          <H1 bold={true} color={theme.colors.greyDarker}>
            We have the solution.
          </H1>
          <HorizontalSpacer spacing={1} />
          <Row style={{ gap: "32px 0px" }} justify="between" align="center">
            <Col lg={3} md={6}>
              <AudienceImage
                src="https://images.ctfassets.net/kzqg847wz5jr/4UcMb7S5HwtrZcMZSdmWg5/c461b8c5ccf03e66fbf9097a5ae7b24d/wwd-infra__1_.webp"
                alt="Satcube serves internet to government and law enforcement"
              />
              <AudienceTitle>
                <Subtitle textAlign="center" color={theme.colors.white}>
                  Government
                </Subtitle>
              </AudienceTitle>
            </Col>
            <Col lg={3} md={6}>
              <AudienceImage
                src="https://images.ctfassets.net/kzqg847wz5jr/3TOWydHOoeRsPojBQw0lBN/01fc3f0f8b047682dfe5aa0425751760/wwd-fire__1_.webp"
                alt="Satcube serves internet to first responders"
              />
              <AudienceTitle>
                <Subtitle textAlign="center" color={theme.colors.white}>
                  First responders
                </Subtitle>
              </AudienceTitle>
            </Col>
            <Col lg={3} md={6}>
              <AudienceImage
                src="https://images.ctfassets.net/kzqg847wz5jr/3NiSLv8NS21oKnaXYLiO1b/a8212d71ae43229bafa68adf31876703/wwd-aid.webp"
                alt="Satcube serves internet to humanitarian aid"
              />
              <AudienceTitle>
                <Subtitle textAlign="center" color={theme.colors.white}>
                  Humanitarian aid
                </Subtitle>
              </AudienceTitle>
            </Col>
            <Col lg={3} md={6}>
              <AudienceImage
                src="https://images.ctfassets.net/kzqg847wz5jr/4UyYaBZfhcc9qb1P4boQn2/3fce289767285d50ad9891ddea981e3a/wwd-broadcast.webp"
                alt="Satcube serves internet to broadcast"
              />
              <AudienceTitle>
                <Subtitle textAlign="center" color={theme.colors.white}>
                  Broadcast
                </Subtitle>
              </AudienceTitle>
            </Col>
          </Row>
          <HorizontalSpacer spacing={5.5} />
        </Container>
      </AudienceSegment>

      <CaseCarousel />
      <Visible xs>
        <HorizontalSpacer spacing={3} />
      </Visible>
      <HowItWorksBanner />
      <KuBanner
        imageSrc={secondSection?.image.url}
        imageAlt={secondSection?.image?.description}
        title={secondSection?.title}
        text={secondSection?.text}
        subtitle={secondSection?.subtitle}
        ButtonTextSmall={secondSection?.buttonTitle}
        buttonUrl={secondSection?.buttonUrl}
      />
      <HorizontalSpacer spacing={4} />
      <Container>
        <H1 bold={true}>Latest news</H1>
        <HorizontalSpacer spacing={2} />
        {loading ? <LoadingSpinner /> : <NewsCarousel news={articles ?? []} />}
        <HorizontalSpacer spacing={4} />
      </Container>
    </div>
  )
}

export default HomePage
